import React, { useRef, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import MarkdownContent from "../markdown/MarkdownContent";
import { MessageData, MessageFile } from "../../types/message";
import { MediaPreviewHandle } from "../../types/media-preview";
import Avatar from "../avatar/Avatar";
import UserAvatar from "../avatar/UserAvatar";
import MediaPreview from "../media-preview/MediaPreview";
import AudioOptionGroup, { AudioOption } from "../audio-preview/AudioOptionGroup";
import { type DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import MessageMetaColumn from "./MessageMetaColumn";
import { buildFolderTree, FolderNode, getTotalFileCount, flattenMessageFilesToPaths } from "./folderUtils";
import FolderContentsModal from "./FolderContentsModal";
import BulkDownloadProgressModal from "./BulkDownloadProgressModal";
import { useBulkDownload } from "../../hooks/useBulkDownload";
import SystemMessageContent from "./SystemMessageContent";
import ToolTip from "../tooltip/ToolTip";
import {
  downloadFile,
  resolveDownloadUrl,
  DOWNLOAD_FAILED_MESSAGE,
} from "../../utils/downloadFile";
import IconButton from "../button/IconButton";
import AttachmentFileCard from "../file-attachment/AttachmentFileCard";
import { getFileIconFromCategoryOrExtension } from "../file-icon/fileIcon";
import { useIsUserOnline } from "../../hooks/useIsUserOnline";
import TakeLabel from "../label/TakeLabel";
import {
  preprocessTimestamps,
  extractFirstTimecode,
  parseTimestampHref,
} from "../../utils/timecode";

const AVATAR_SIZE = 32;

// 型定義
interface MessageItemProps {
  message: MessageData;
  isOurs?: boolean;
  isSystem?: boolean;
  /**
   * アバター表示オプション。連投時の省略判定に使う。
   * 自分側（右）では、投稿者が `currentUserId` 以外のとき（同じ陣営の別メンバー）のみ
   * 実際にアバターが表示される。
   */
  showAvatar?: boolean;
  showActions?: boolean; // アクションメニュー表示オプション
  parentMessage?: MessageData | null; // 返信元メッセージ
  currentUserId?: number | string; // 現在のユーザーID（既読表示のフィルタリング / 自分側アバターの本人判定用）
  onReply?: (message: MessageData) => void;
  onEdit?: (message: MessageData) => void;
  onDelete?: (message: MessageData) => void;
  onResolve?: (message: MessageData) => void;
  /**
   * 「リンクをコピー」。渡されたときだけメニューに出す
   * （ショットコメントのみ。トークルーム / DM は対象外）。
   */
  onCopyLink?: (message: MessageData) => void;
  onPinClick?: (messageId: string, mediaItemId: string, timecodeStart: string) => void; // ピンアイコンクリック時
  /**
   * [M:SS] / [M:SS.FF] / [N]（絶対フレーム）クリック時。
   * seconds は整数秒（フレーム部を含まない）、frames はフレーム番号（無ければ null）。
   * 絶対フレーム [N] は seconds = 0 + frames = N として渡される。
   * fps は受け手側で対象メディアから解決して seconds + frames / fps を計算する。
   */
  onTimestampClick?: (seconds: number, mediaItemId: string | null, frames?: number | null) => void;
  pinTooltip?: string; // ピンアイコンのツールチップ（例: "0:01 バリエーションA"）
  playingMessageId?: string | null; // 再生中のメッセージID（play_circle / stop_circle 切替用）
  onFileDownload?: (messageId: string, fileId: string) => void; // ファイルダウンロード記録コールバック
  compactMediaPreview?: boolean; // true の場合、添付メディアをコンパクト表示
  onMediaRef?: (messageId: string, fileId: string, handle: MediaPreviewHandle | null) => void;
  onPlayStateChange?: (playing: boolean) => void;
  filterMode?: "in_progress" | "completed";
  /** 納品物（ファイルメッセージ）の右上に表示するテイク番号。未指定なら非表示 */
  take?: number;
}

// 相手のメッセージコンテナ - モバイル対応
const MessageContainerTheir = styled.div`
  padding: 0px 0px 8px 0px;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  position: relative;
  container-type: inline-size;

  &:hover .message-actions {
    visibility: visible;
    opacity: 1;
  }
`;

// 自分のメッセージコンテナ - モバイル対応
const MessageContainerOurs = styled.div`
  padding: 0px 0px 8px 0px;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  position: relative;
  container-type: inline-size;

  &:hover .message-actions {
    visibility: visible;
    opacity: 1;
  }
`;

// アバターラッパー（上揃え用）
const AvatarWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
`;

// 連投時のアバター省略用スペーサー（アバターと同じ幅で左揃えを維持）
const AvatarSpacer = styled.div`
  width: ${AVATAR_SIZE}px;
  flex-shrink: 0;
`;

// アクション列（3 点メニュー + 作成日/既読日時 + 既読アイコン）は
// `MessageMetaColumn` に共通化済み（契約書アイテムと表示を統一するため）。

// ファイル添付コンテナ
const AttachedFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  width: 100%;
`;

// ダウンロード既読アバターコンテナ
const DownloadReceiptsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  padding: 2px 4px;

  & > * {
    margin-left: -4px;
    
    &:first-child {
      margin-left: 0;
    }
  }

  & > * > div {
    box-shadow: none;
  }
`;

// フォルダアイテム
const FolderItem = styled.div.attrs({
  className: 'bodytext-11 u-line-height-100',
})`
  padding: 8px 8px 8px 6px;
  background-color: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  
  &:hover {
    background-color: var(--soremo-background);
    border-color: var(--soremo-blue);
  }
  
  .folder-icon {
    font-size: 20px;
    color: var(--soremo-light-gray);
    flex-shrink: 0;
  }
`;

// フォルダ名
const FolderName = styled.span.attrs({
  className: 'bodytext-11 u-line-height-150',
})`
  flex: 1;
  color: var(--soremo-black);
`;

// フォルダ内ファイル数
const FolderFileCount = styled.span.attrs({
  className: 'bodytext-11 u-line-height-100',
})`
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

// 相手のメッセージ
const MessageTheir = styled.div.attrs({
  className: 'bodytext-13 u-line-height-150',
})<{ $hasMediaFiles?: boolean }>`
  position: relative;
  padding: 8px 8px;
  border-radius: 6px;
  background: radial-gradient(50% 50% at 50% 50%, #fff 0%, #fcfcfc 100%);
  border: 1px solid var(--soremo-border);
  word-break: break-word;
  max-width: 75%;
  ${props => props.$hasMediaFiles && `
    width: fit-content;
  `}
`;

// 自分のメッセージ
const MessageOurs = styled.div.attrs({
  className: 'bodytext-13 u-line-height-150',
})<{ $hasMediaFiles?: boolean }>`
  position: relative;
  padding: 8px 8px;
  border-radius: 6px;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(252, 252, 252, 0.05) 0%,
    rgba(0, 154, 206, 0.05) 100%
  );
  border: 1px solid rgba(0, 154, 206, 0.1);
  color: black;
  word-break: break-word;
  max-width: 75%;
  ${props => props.$hasMediaFiles && `
    width: fit-content;
  `}
`;

// システムメッセージ（中央寄せのコンテナ。見た目は SystemMessageContent の pill が担う）
const MessageSystem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 8px 0;
`;

// 親メッセージ（返信元）コンテナ
const ParentMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
  margin-bottom: 8px;
  border-left: 3px solid var(--soremo-border);
`;

// 親メッセージの投稿者セクション（アバター + 名前）
const ParentAuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
`;

// 親メッセージの投稿者名
const ParentAuthorName = styled.span.attrs({
  className: 'bodytext-11 u-line-height-100',
})`
  color: var(--soremo-gray);
`;

// 親メッセージの引用テキスト
const ParentQuoteText = styled.div.attrs({
  className: 'bodytext-11 u-line-height-150',
})`
  color: var(--soremo-light-gray);
  white-space: pre-wrap;
  word-break: break-word;
  /* 長いテキストは省略表示 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// 再生アイコン（ピン付きコメントの先頭に表示）
const PlayIcon = styled.span`
  color: var(--soremo-blue);
  cursor: pointer;
  font-size: 24px;
  vertical-align: middle;
  margin-right: 2px;
  line-height: 1;
  transition: color 0.15s;

  &:hover {
    color: var(--soremo-deep-blue);
  }
`;


/**
 * メッセージアイテムコンポーネント
 *
 * トークルーム（ProjectMessage）、プレビュー（ShotComment）共通。
 * filterMode="completed" の場合、三点メニューの代わりに task_alt アイコンを表示し、
 * ワンクリックで進行中に戻せる。
 */
const MessageItem: React.FC<MessageItemProps> = ({ 
  message, 
  isOurs = false, 
  isSystem = false,
  showAvatar = true,
  showActions = true,
  parentMessage = null,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
  onResolve,
  onCopyLink,
  onPinClick,
  onTimestampClick,
  pinTooltip,
  playingMessageId,
  onFileDownload,
  compactMediaPreview = false,
  onMediaRef,
  onPlayStateChange,
  filterMode = "in_progress",
  take,
}) => {
  const isCompleted = filterMode === "completed";
  // コンテナへの参照を作成
  const containerRef = useRef<HTMLDivElement>(null);

  // 相手メッセージのアバターにオンラインバッジを出すための判定
  // (MessageUser.id は number | string 混在のためフック側で正規化)
  const isAuthorOnline = useIsUserOnline(message.author?.id);

  // 自分側（右）は同じ陣営（position グループ）のメンバーの投稿も含むため、
  // 投稿者が自分以外なら誰の発言か分かるようアバターを出す。
  // currentUserId が無いと本人判定ができないので、その場合は従来どおり非表示。
  const showOursAvatar =
    isOurs &&
    showAvatar &&
    !!message.author &&
    currentUserId != null &&
    String(message.author.id) !== String(currentUserId);

  // video/audio の MediaPreview ref を安定的に親へ通知するための callback ref マップ
  const mediaRefCallbacks = useRef<Record<string, (handle: MediaPreviewHandle | null) => void>>({});
  const onMediaRefLatest = useRef(onMediaRef);
  onMediaRefLatest.current = onMediaRef;

  const getMediaRefCallback = useCallback((fileId: string) => {
    if (!mediaRefCallbacks.current[fileId]) {
      mediaRefCallbacks.current[fileId] = (handle: MediaPreviewHandle | null) => {
        onMediaRefLatest.current?.(message.id, fileId, handle);
      };
    }
    return mediaRefCallbacks.current[fileId];
  }, [message.id]);
  
  // フォルダモーダル用のstate
  const [selectedFolder, setSelectedFolder] = useState<FolderNode | null>(null);

  // 取得に成功したファイルの既読登録
  const handleFileDownloaded = useCallback((fileId: string) => {
    if (onFileDownload) {
      onFileDownload(message.id, fileId);
    }
  }, [onFileDownload, message.id]);

  // プレビュー不可ファイルカードの単体ダウンロード
  // download_url（attachment 付き presigned URL）を優先し、失敗時はトーストで通知する。
  // ダウンロードを開始できたときのみ既読登録する。
  const handleFileCardDownload = useCallback(async (file: MessageFile) => {
    const url = resolveDownloadUrl(file);
    if (!url) return;
    const succeeded = await downloadFile(url, file.file_name);
    if (!succeeded) {
      toast.error(DOWNLOAD_FAILED_MESSAGE);
      return;
    }
    handleFileDownloaded(file.id);
  }, [handleFileDownloaded]);

  // メッセージ全添付ファイルの一括ダウンロード進捗
  const {
    downloadState,
    isDownloading: isDownloadingAllFiles,
    startDownload,
    closeDownloadModal,
  } = useBulkDownload(handleFileDownloaded);

  // zip 名（例: "添付ファイル_20260713-2200"）。メッセージ送信日時から生成する。
  const bulkDownloadName = useMemo(() => {
    const createdAt = new Date(message.created_at);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `添付ファイル_${createdAt.getFullYear()}${pad(createdAt.getMonth() + 1)}${pad(createdAt.getDate())}-${pad(createdAt.getHours())}${pad(createdAt.getMinutes())}`;
  }, [message.created_at]);

  // メッセージの全添付ファイルを zip でまとめてダウンロード + 既読登録
  const handleDownloadAllFiles = useCallback(() => {
    startDownload(flattenMessageFilesToPaths(message.files ?? []), bulkDownloadName);
  }, [startDownload, message.files, bulkDownloadName]);
  
  // フォルダ構造を構築（メモ化）
  const folderStructure = useMemo(() => {
    if (!message.has_files || !message.files || message.files.length === 0) {
      return null;
    }
    return buildFolderTree(message.files);
  }, [message.files, message.has_files]);
  
  // メディアファイル（画像、動画、オーディオ、PDF）があるかどうかを判定
  // renderAttachedFilesと同じロジックで、filesWithoutFolderのプレビュー可能ファイルをチェック
  const hasMediaFiles = useMemo(() => {
    if (!folderStructure) return false;
    const { filesWithoutFolder } = folderStructure;
    return filesWithoutFolder.some((file: MessageFile) => {
      const fileType = file.file_type;
      return ['image', 'video', 'audio', 'document'].includes(fileType || '');
    });
  }, [folderStructure]);

  // ===== オプションモード（複数オーディオ添付の比較試聴グループ） =====
  // フォルダ外のオーディオ添付が 2 件以上あるとき、個別プレイヤーではなく
  // AudioOptionGroup（A/B/C… の聴き比べカード）にまとめて表示する
  const audioOptionData = useMemo(() => {
    if (compactMediaPreview || !folderStructure) return null;
    const audioFiles = folderStructure.filesWithoutFolder.filter(
      (f: MessageFile) => f.file_type === 'audio' && !!(f.optimized_url || f.original_url),
    );
    if (audioFiles.length < 2) return null;

    const options: AudioOption[] = audioFiles.map((f: MessageFile) => ({
      id: f.id,
      src: (f.optimized_url || f.original_url) as string,
      fileName: f.file_name,
      waveformUrl: f.has_waveform && f.waveform_url ? f.waveform_url : undefined,
      downloadUrl: resolveDownloadUrl(f) ?? undefined,
      acrScanStatus: f.acr_scan_status,
      acrScanResult: f.acr_scan_result,
      acrScannedAt: f.acr_scanned_at,
      footer: f.downloaded_users && f.downloaded_users.length > 0 ? (
        <DownloadReceiptsContainer>
          {f.downloaded_users.map((user) => (
            <UserAvatar
              key={user.id}
              userId={user.id}
              avatar={user.avatar}
              displayName={user.display_name}
              size={12}
            />
          ))}
        </DownloadReceiptsContainer>
      ) : undefined,
    }));

    return { firstAudioId: audioFiles[0].id, options };
  }, [compactMediaPreview, folderStructure]);

  // オプション行の再生ハンドルを親（onMediaRef）へ中継する
  const registerAudioOptionHandle = useCallback(
    (optionId: string, handle: MediaPreviewHandle | null) => {
      getMediaRefCallback(optionId)(handle);
    },
    [getMediaRefCallback],
  );

  // オプション行のダウンロード記録（既読登録）
  const handleAudioOptionDownloaded = useCallback(
    (option: AudioOption) => {
      handleFileDownloaded(option.id);
    },
    [handleFileDownloaded],
  );
  
  // フォルダクリックハンドラ
  const handleFolderClick = useCallback((folder: FolderNode) => {
    setSelectedFolder(folder);
  }, []);
  
  // モーダルを閉じる
  const handleCloseModal = useCallback(() => {
    setSelectedFolder(null);
  }, []);

  // 投稿者アバター（左右共通）
  const renderAuthorAvatar = () =>
    message.author ? (
      <AvatarWrapper>
        <Avatar
          size={AVATAR_SIZE}
          src={message.author.avatar || undefined}
          tooltipText={message.author.display_name}
          tooltipPlacement="bottom"
          name={message.author.display_name}
          isOnline={isAuthorOnline}
        />
      </AvatarWrapper>
    ) : null;

  // 親メッセージ（返信元）のレンダリング
  const renderParentMessage = (parentMsg: MessageData) => {
    const authorName = parentMsg.author?.display_name || '不明';
    const avatarSrc = parentMsg.author?.avatar || undefined;

    return (
      <ParentMessageContainer>
        <ParentAuthorSection>
          <Avatar
            size={24}
            src={avatarSrc}
            name={authorName}
          />
          <ParentAuthorName>{authorName}</ParentAuthorName>
        </ParentAuthorSection>
        <ParentQuoteText>{parentMsg.message}</ParentQuoteText>
      </ParentMessageContainer>
    );
  };

  const isPlaying = playingMessageId === message.id;

  // 再生アイコンクリックハンドラ（停止判定は Preview 側で messageId を見て行う）
  const handlePlayIconClick = useCallback(() => {
    if (message.media_item_id && onPinClick) {
      onPinClick(message.id, message.media_item_id, extractFirstTimecode(message.message));
    }
  }, [message.id, message.media_item_id, message.message, onPinClick]);

  // タイムスタンプリンクのイベント委任ハンドラ（href 属性で判定、class 不要）
  const handleMarkdownClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href?.startsWith('#ts-')) {
        e.preventDefault();
        e.stopPropagation();
        if (onTimestampClick) {
          const parts = parseTimestampHref(href);
          if (parts) {
            onTimestampClick(parts.minutes * 60 + parts.seconds, message.media_item_id ?? null, parts.frames);
          }
        }
      }
    }
  }, [onTimestampClick, message.media_item_id]);

  // マークダウンレンダラー
  const renderMessageContent = (text: string) => {
    const processedText = onTimestampClick ? preprocessTimestamps(text) : text;

    return (
      <>
        {parentMessage && renderParentMessage(parentMessage)}
        <MarkdownContent
          content={processedText}
          containerRef={containerRef}
          onClick={handleMarkdownClick}
          prefix={
            /*
              メッセージ本文先頭のピン再生アイコン。
              このアイコンは「バリエーションの再生トリガー」であり、
              添付ファイル名の先頭に置くファイルタイプアイコン/再生アイコン
              （src/components/file-icon/fileIcon.ts のルール）とは別概念である。
            */
            message.media_item_id && onPinClick ? (
              <ToolTip description={pinTooltip || ''} placement="right" appendTo="parent">
                <PlayIcon
                  className="material-symbols-rounded"
                  onClick={handlePlayIconClick}
                >
                  {isPlaying ? 'stop_circle' : 'play_circle'}
                </PlayIcon>
              </ToolTip>
            ) : null
          }
        />
      </>
    );
  };

  // 添付ファイルのレンダリング
  const renderAttachedFiles = () => {
    if (!folderStructure) {
      return null;
    }

    const { rootFolders, filesWithoutFolder } = folderStructure;

    return (
      <>
        <AttachedFilesContainer>
          {/* ルートフォルダを表示（第一階層） */}
          {rootFolders.map((folder: FolderNode) => {
            const totalFiles = getTotalFileCount(folder);
            return (
              <FolderItem
                key={folder.id}
                onClick={() => handleFolderClick(folder)}
              >
                <span className="material-symbols-rounded folder-icon">folder</span>
                <FolderName>{folder.name}</FolderName>
                <FolderFileCount>{totalFiles}件</FolderFileCount>
              </FolderItem>
            );
          })}
          
          {/* フォルダに属さないファイル（ファイル名昇順） */}
          {filesWithoutFolder.map((file: MessageFile, index: number) => {
            const fileUrl = file.optimized_url || file.original_url;
            const fileName = file.file_name;
            const fileType = file.file_type;
            const fileId = file.id;

            // メディアプレビュー可能なファイルタイプ
            const isPreviewable = ['image', 'video', 'audio', 'document'].includes(fileType || '');

            // オプションモード: 複数オーディオはグループカードにまとめる
            // （先頭のオーディオ位置に 1 回だけ描画し、残りはスキップ）
            if (audioOptionData && fileType === 'audio' && fileUrl) {
              if (fileId !== audioOptionData.firstAudioId) {
                return null;
              }
              return (
                <AudioOptionGroup
                  key="audio-option-group"
                  options={audioOptionData.options}
                  onDownloaded={handleAudioOptionDownloaded}
                  onPlayStateChange={onPlayStateChange}
                  registerHandle={registerAudioOptionHandle}
                  style={{ width: 'clamp(256px, 61.8cqi, 720px)' }}
                />
              );
            }

            // MediaPreviewの場合はFileItemでラップしない
            if (isPreviewable && fileUrl) {
              const isAudioOrVideo = fileType === 'video' || fileType === 'audio';
              return (
                <div key={fileId || index}>
                  <MediaPreview
                    ref={isAudioOrVideo && fileId ? getMediaRefCallback(fileId) : undefined}
                    src={fileUrl}
                    thumbnailSrc={file.thumbnail || undefined}
                    downloadUrl={resolveDownloadUrl(file) ?? undefined}
                    mediaType={fileType === 'document' ? 'pdf' : fileType as any}
                    variant={compactMediaPreview ? 'compact' : 'default'}
                    sizeMode={{ 
                      mode: 'fixed-width', 
                      width: '100%', 
                      maxHeight: '100%'
                    }}
                    style={{ width: 'clamp(256px, 61.8cqi, 720px)' }}
                    showMenu={fileType === 'audio'}
                    menuOutside={fileType === 'audio'}
                    hoverPlay={false}
                    showWaveform={true}
                    waveformUrl={file.has_waveform && file.waveform_url ? file.waveform_url : undefined}
                    fileName={fileName}
                    createdAt={message.created_at}
                    disableHoverEffect={true}
                    enableClickPreview={true}
                    compactAudio={fileType === 'audio'}
                    fileId={fileId}
                    acrScanStatus={file.acr_scan_status}
                    acrScanResult={file.acr_scan_result}
                    acrScannedAt={file.acr_scanned_at}
                    onDownload={() => {
                      if (onFileDownload && fileId) {
                        onFileDownload(message.id, fileId);
                      }
                    }}
                    onPlayStateChange={isAudioOrVideo ? onPlayStateChange : undefined}
                  />
                  {file.downloaded_users && file.downloaded_users.length > 0 && (
                    <DownloadReceiptsContainer>
                      {file.downloaded_users.map((user) => (
                        <UserAvatar
                          key={user.id}
                          userId={user.id}
                          avatar={user.avatar}
                          displayName={user.display_name}
                          size={12}
                        />
                      ))}
                    </DownloadReceiptsContainer>
                  )}
                </div>
              );
            }

            // プレビュー不可のファイルは添付ファイルカードでラップ
            const cardDownloadUrl = resolveDownloadUrl(file);
            return (
              <AttachmentFileCard
                key={fileId || index}
                fileName={fileName}
                icon={getFileIconFromCategoryOrExtension(fileType, fileName)}
                downloadDisabled={!cardDownloadUrl}
                downloadDisabledReason="このファイルはダウンロードできません（URLがありません）"
                onDownload={() => {
                  void handleFileCardDownload(file);
                }}
              >
                {file.downloaded_users && file.downloaded_users.length > 0 && (
                  <DownloadReceiptsContainer>
                    {file.downloaded_users.map((user) => (
                      <UserAvatar
                        key={user.id}
                        userId={user.id}
                        avatar={user.avatar}
                        displayName={user.display_name}
                        size={12}
                      />
                    ))}
                  </DownloadReceiptsContainer>
                )}
              </AttachmentFileCard>
            );
          })}
        </AttachedFilesContainer>
        
        {/* フォルダ内容モーダル */}
        {selectedFolder && (
          <FolderContentsModal
            folder={selectedFolder}
            onClose={handleCloseModal}
            messageId={message.id}
            onFileDownload={onFileDownload}
          />
        )}

        {/* 全添付ファイル一括ダウンロードの進捗・結果モーダル */}
        {downloadState && (
          <BulkDownloadProgressModal
            state={downloadState}
            downloadName={bulkDownloadName}
            onClose={closeDownloadModal}
          />
        )}
      </>
    );
  };

  // アクションメニューのレンダリング
  const renderActions = () => {
    // メニューアイテムを動的に構築
    const menuItems: DropdownMenuItem[] = [];
    
    if (onResolve && !isCompleted) {
      menuItems.push({
        label: message.is_resolved ? "進行中に戻す" : "解決済みにする",
        onClick: () => onResolve(message),
        icon: "task_alt",
      });
    }
    
    if (onReply) {
      menuItems.push({
        label: "返信",
        onClick: () => onReply(message),
        icon: "reply",
      });
    }

    if (onCopyLink) {
      menuItems.push({
        label: "リンクをコピー",
        onClick: () => onCopyLink(message),
        icon: "link",
      });
    }

    // 添付ファイルが2件以上あるとき、全添付ファイルの zip 一括ダウンロードを出す
    if (message.files && message.files.length >= 2) {
      menuItems.push({
        label: "一括ダウンロード",
        onClick: handleDownloadAllFiles,
        icon: "download",
        disabled: isDownloadingAllFiles,
      });
    }
    
    if (onEdit && isOurs) {
      menuItems.push({
        label: "編集",
        onClick: () => onEdit(message),
        icon: "edit",
      });
    }
    
    if (onDelete && isOurs) {
      menuItems.push({
        label: "削除",
        onClick: () => onDelete(message),
        icon: "delete",
      });
    }

    // 完了フィルタ表示中は上段に「進行中に戻す」アイコンを出す。
    const completedTopSlot =
      isCompleted && onResolve ? (
        <ToolTip description="進行中に戻す" placement="top" appendTo="parent">
          <IconButton
            icon="task_alt"
            variant="circle"
            filled
            onClick={() => onResolve(message)}
          />
        </ToolTip>
      ) : undefined;

    return (
      <MessageMetaColumn
        isOurs={isOurs}
        createdAt={message.created_at}
        receivers={message.receivers}
        currentUserId={currentUserId}
        menuItems={menuItems}
        showMenu={!isCompleted && showActions && menuItems.length > 0}
        topSlot={completedTopSlot}
      />
    );
  };

  // システムメッセージの場合
  // - DM 由来の場合は `message.system_content` (Discriminated Union) を見て
  //   `offer_summary` / `offer_accepted` / `plain` を切り替える。
  // - それ以外（system_content が無い既存メッセージ）は従来どおり message.message を表示。
  if (isSystem) {
    if (message.system_content) {
      return (
        <MessageSystem>
          <SystemMessageContent
            systemContent={message.system_content}
            fallbackText={message.message}
          />
        </MessageSystem>
      );
    }
    return (
      <MessageSystem>
        {renderMessageContent(message.message)}
      </MessageSystem>
    );
  }

  // 自分のメッセージ表示
  if (isOurs) {
    return (
      <MessageContainerOurs>
        {renderActions()}
        <MessageOurs $hasMediaFiles={hasMediaFiles}>
          {take !== undefined && <TakeLabel take={take} size="small" />}
          {renderMessageContent(message.message)}
          {renderAttachedFiles()}
        </MessageOurs>
        {showOursAvatar && renderAuthorAvatar()}
      </MessageContainerOurs>
    );
  }

  // 相手のメッセージ表示
  return (
    <MessageContainerTheir>
      {showAvatar && message.author ? renderAuthorAvatar() : <AvatarSpacer />}
      <MessageTheir $hasMediaFiles={hasMediaFiles}>
        {take !== undefined && <TakeLabel take={take} size="small" />}
        {renderMessageContent(message.message)}
        {renderAttachedFiles()}
      </MessageTheir>
      {renderActions()}
    </MessageContainerTheir>
  );
};

export default MessageItem;

